import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff, KeyRound, ArrowLeft, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'

export default function ResetPassword() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password !== confirm) {
      toast({ title: t('auth.passwordsDoNotMatch'), variant: 'destructive' })
      return
    }
    if (!token) {
      toast({ title: t('auth.invalidToken'), variant: 'destructive' })
      return
    }
    setLoading(true)
    try {
      await api.post('/auth/reset-password', { token, password })
      setDone(true)
      toast({ title: t('auth.passwordResetSuccess') })
      setTimeout(() => navigate('/login'), 2000)
    } catch (err: any) {
      toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
        <div className="text-center text-muted-foreground">
          <p className="mb-4">{t('auth.invalidToken')}</p>
          <Link to="/forgot-password" className="text-primary hover:underline">{t('auth.requestNewLink')}</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-display text-2xl font-bold">{t('auth.resetPassword')}</h1>
          <p className="text-muted-foreground mt-2">{t('auth.resetSubtitle')}</p>
        </div>

        {done ? (
          <div className="p-6 rounded-2xl bg-card border border-border text-center space-y-4">
            <CheckCircle className="w-10 h-10 text-green-500 mx-auto" />
            <p className="text-sm text-muted-foreground">{t('auth.passwordResetSuccess')}</p>
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
              <ArrowLeft className="w-4 h-4" /> {t('auth.backToLogin')}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('auth.newPassword')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 pr-12"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('auth.confirmPassword')}</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <Button type="submit" className="w-full rounded-lg" disabled={loading}>
              <KeyRound className="w-4 h-4 mr-2" />
              {loading ? t('common.updating') : t('auth.updatePassword')}
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  )
}
